import React, { useState, useEffect, useMemo } from 'react';
import Loading from '../../Common/Loading';

// --- IMPORTS FOR IMAGES ---
import go_n from './Images/go_n.gif';
import go_o from './Images/go_o.gif';
import go_d from './Images/go_d.gif';
import back_n from './Images/back_n.gif';
import back_o from './Images/back_o.gif';
import back_d from './Images/back_d.gif';

const btnImages: Record<string, string> = {
    'go_n': go_n, 'go_o': go_o, 'go_d': go_d,
    'back_n': back_n, 'back_o': back_o, 'back_d': back_d
};

// --- Interfaces ---
interface CPCRecord {
    installer: string;
    installer_login: string;
    installations: number;
    cpc_before_inst: number;
    etc: number;
    etc_perc: number;
    mtc: number;
    mtc_perc: number;
    cpc: number;
    cpc_perc: number;
    total: number;
    total_perc: number;
    total_sales_count: number;
}

type SortKey = keyof CPCRecord;

// --- Helper: Legacy Image Button Component ---
const ImageButton: React.FC<{ name: string; onClick?: () => void; style?: React.CSSProperties }> = ({ name, onClick, style }) => {
    const [suffix, setSuffix] = useState('n');
    const imageKey = `${name}_${suffix}`;
    const imageSrc = btnImages[imageKey];

    return (
        <img 
            src={imageSrc} alt={name}
            style={{ cursor: 'pointer', verticalAlign: 'middle', ...style }}
            onMouseEnter={() => setSuffix('o')} 
            onMouseLeave={() => setSuffix('n')} 
            onMouseDown={() => setSuffix('d')}  
            onMouseUp={() => setSuffix('o')}    
            onClick={onClick} draggable={false}
        />
    );
};

const CPCInstallersReport: React.FC = () => {
   const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

    const getFormattedDate = (date: Date) => date.toISOString().split('T')[0];
    const today = new Date();

    // --- State ---
    const [fromDate, setFromDate] = useState(getFormattedDate(today));
    const [toDate, setToDate] = useState(getFormattedDate(today));
    const [country, setCountry] = useState('---ALL---');
    const [countries, setCountries] = useState<string[]>([]);
    
    const [data, setData] = useState<CPCRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: 'asc' | 'desc' } | null>(null);

    // --- Effects ---
    useEffect(() => {
        document.body.style.backgroundColor = '#ffffff';
        const fetchCountries = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}api/get_dlr_countries`);
                const json = await res.json();
                if (json.countries) setCountries(json.countries);
            } catch (err) { console.error(err); }
        };
        fetchCountries();
        handleGo(); // Auto load
    }, []);

    // --- Handlers ---
    const handleGo = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/cpc_installers_report`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ country, from_date: fromDate, to_date: toDate })
            });
            const json = await res.json();
            if (json.error) alert(json.error);
            else setData(json.results || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        window.location.href = "/Sysadmin";
    };

    const handleRowClick = (record: CPCRecord) => {
        if (record.cpc > 0) {
            const fParts = fromDate.split('-');
            const tParts = toDate.split('-');
            const fStr = `${fParts[2]}/${fParts[1]}/${fParts[0]}`;
            const tStr = `${tParts[2]}/${tParts[1]}/${tParts[0]}`;
            
            const url = `/SYSADMIN/Reports/CPCCustomersReport?InstallerLogin=${record.installer_login}&FromDate=${fStr}&ToDate=${tStr}&Country=${country}&`;
            window.open(url, '_newtab');
        } else {
            alert('No CPC for this Installer.');
        }
    };

    const handleSort = (key: SortKey) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedData = useMemo(() => {
        let sortableItems = [...data];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
                if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [data, sortConfig]);

    // Calculate Footer Totals
    const totals = useMemo(() => {
        return data.reduce((acc, row) => {
            acc.installations += row.installations;
            acc.cpc_before_inst += row.cpc_before_inst;
            acc.etc += row.etc;
            acc.mtc += row.mtc;
            acc.cpc += row.cpc;
            acc.total += row.total;
            acc.total_sales_count += row.total_sales_count;
            return acc;
        }, { installations: 0, cpc_before_inst: 0, etc: 0, mtc: 0, cpc: 0, total: 0, total_sales_count: 0 });
    }, [data]);

    // Footer Percentages
    const footerEtcPerc = totals.total_sales_count ? ((totals.etc / totals.total_sales_count) * 100).toFixed(2) : "";
    const footerMtcPerc = totals.total_sales_count ? ((totals.mtc / totals.total_sales_count) * 100).toFixed(2) : "";
    const footerCpcPerc = totals.total_sales_count ? ((totals.cpc / totals.total_sales_count) * 100).toFixed(2) : "";
    const footerTotalPerc = totals.total_sales_count ? ((totals.total / totals.total_sales_count) * 100).toFixed(2) : "";

    // --- Exact Styles Matching Screenshot ---
    const styles = {
        body: { fontFamily: 'Times New Roman, serif', textAlign: 'center' as const, minHeight: '100vh', padding: '10px' },
        h1: { fontSize: '28px', fontWeight: 'bold', margin: '20px 0' },
        calContainer: { padding: '5px', textAlign: 'center' as const, marginBottom: '10px', fontFamily: 'Arial, sans-serif' },
        input: { fontFamily: 'Arial', fontSize: '13px', padding: '2px', border: '1px solid #7f9db9', width: '130px', marginRight: '5px' },
        select: { fontFamily: 'Arial', fontSize: '13px', padding: '2px', border: '1px solid #7f9db9', width: '130px', marginLeft: '10px' },
        btnPlaceholder: { padding: '10px 0', textAlign: 'center' as const, marginBottom: '15px' },
        
        // Grid Styles
        gridWrapper: { overflowX: 'auto' as const, overflowY: 'auto' as const, maxHeight: '850px', width: '100%', border: '0px solid #072129' },
        table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '13px' },
        
        // Cyan Header matching #45d4ff from CSS
        th: { fontSize:"18px",  backgroundColor: '#45d4ff', color: '#000', padding: '8px 20px', border: '1px solid #072129', cursor: 'pointer', whiteSpace: 'nowrap' as const, fontWeight: 'bold', textDecoration: 'underline' },
        td: { fontSize:"15px",  padding: '4px', border: '1px solid #072129', color: 'black', textAlign: 'center' as const },
        footerRow: { backgroundColor: '#E5F2FF', fontWeight: 'bold' } 
    };

    return (
        <div style={styles.body}>
            {loading && <Loading />}

            <h1 style={styles.h1}>CPC Installers Report</h1>

            <div style={styles.calContainer}>
                <input type="date" style={styles.input} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                <span style={{ fontSize: '13px', margin: '0 5px' }}>To: </span>
                <input type="date" style={styles.input} value={toDate} onChange={(e) => setToDate(e.target.value)} />
                
                <select style={styles.select} value={country} onChange={(e) => setCountry(e.target.value)}>
                    <option value="---ALL---">---ALL---</option>
                    {countries.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
            </div>

            <div style={styles.btnPlaceholder}>
                <ImageButton name="go" onClick={handleGo} style={{ marginRight: '10px' }} />
                <ImageButton name="back" onClick={handleBack} />
            </div>

            {/* Always render table, even if data is empty, to show the Total row */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: '95%', maxWidth: '1400px' }}>
                    <div style={styles.gridWrapper}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Sl No</th>
                                    <th style={styles.th} onClick={() => handleSort('installer')}>Installer</th>
                                    <th style={styles.th} onClick={() => handleSort('installations')}># Installations</th>
                                    <th style={styles.th} onClick={() => handleSort('cpc_before_inst')}>CPC (Before Inst.)</th>
                                    <th style={styles.th} onClick={() => handleSort('etc')}># ETC</th>
                                    <th style={styles.th} onClick={() => handleSort('etc_perc')}>ETC %</th>
                                    <th style={styles.th} onClick={() => handleSort('mtc')}># MTC</th>
                                    <th style={styles.th} onClick={() => handleSort('mtc_perc')}>MTC %</th>
                                    <th style={styles.th} onClick={() => handleSort('cpc')}># CPC</th>
                                    <th style={styles.th} onClick={() => handleSort('cpc_perc')}>CPC %</th>
                                    <th style={styles.th} onClick={() => handleSort('total')}>#Total</th>
                                    <th style={styles.th} onClick={() => handleSort('total_perc')}>Total %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedData.map((row, idx) => (
                                    <tr 
                                        key={idx} 
                                        style={{ backgroundColor: idx % 2 === 0 ? '#E5F2FF' : '#ffffff', cursor: 'pointer' }}
                                        onClick={() => handleRowClick(row)}
                                    >
                                        <td style={styles.td}>{idx + 1}</td>
                                        <td style={{ ...styles.td, textAlign: 'left'}}>{row.installer}</td>
                                        <td style={styles.td}>{row.installations}</td>
                                        <td style={styles.td}>{row.cpc_before_inst}</td>
                                        <td style={styles.td}>{row.etc}</td>
                                        <td style={styles.td}>{row.etc_perc}</td>
                                        <td style={styles.td}>{row.mtc}</td>
                                        <td style={styles.td}>{row.mtc_perc}</td>
                                        <td style={styles.td}>{row.cpc}</td>
                                        <td style={styles.td}>{row.cpc_perc}</td>
                                        <td style={styles.td}>{row.total}</td>
                                        <td style={styles.td}>{row.total_perc}</td>
                                    </tr>
                                ))}
                                
                                {/* Footer Totals Row -> Always visible as Row 1 if data is empty */}
                                <tr style={styles.footerRow}>
                                    <td style={styles.td}>{data.length + 1}</td>
                                    <td style={{ ...styles.td, textAlign: 'left' }}>Total</td>
                                    <td style={styles.td}>{totals.installations}</td>
                                    <td style={styles.td}>{totals.cpc_before_inst}</td>
                                    <td style={styles.td}>{totals.etc}</td>
                                    <td style={styles.td}>{footerEtcPerc}</td>
                                    <td style={styles.td}>{totals.mtc}</td>
                                    <td style={styles.td}>{footerMtcPerc}</td>
                                    <td style={styles.td}>{totals.cpc}</td>
                                    <td style={styles.td}>{footerCpcPerc}</td>
                                    <td style={styles.td}>{totals.total}</td>
                                    <td style={styles.td}>{footerTotalPerc}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CPCInstallersReport;